import React, {Component} from "react";
import ConfirmDialog from "./common/ConfirmDialog";
import { strings } from "../localization";
import { TAG_USER_ROLES, TAG_TYPES_BADGES, TAG_ROLES_ICONS, TAG_USER_TYPES } from "../data/tags";
import TagRolesDropDown from "./TagRolesDropDown";
import InfoTooltip from "./InfoTooltip";
import TagsUtil from "./common/tagsUtils";

const MORE_TAGS_COUNT_WIDTh = 52; // corresponds to the width of the div.more-tags-count in _tags.scss
const TAGS_LIST_GAP = 7; // corresponds to the gap in .tags-list-linear in _tags.scss

export class TagBadge extends Component {
    render() {
        const {tagType} = this.props;
        const badge = TAG_TYPES_BADGES[tagType];
        const title = strings[`tags_type_${tagType.toLowerCase()}_label`]
        return <i title={title} className="tag-badge">{badge ? badge: ' '}</i>
    }
}

export class TagRoleBadge extends Component {
    render() {
        const {role} = this.props;
        const icon = TAG_ROLES_ICONS[role];
        const title = strings[`tags_role_${role.toLowerCase()}_label`]
        return <i title={title} className="tag-role">{icon ? icon: ' '}</i>
    }
}

class TagForDisplay extends Component {

    onUnassign = () => {
        this.props.onUnassign(this.props.tag);
    }

    render() {
        const {tag, compact, readOnly, canUnassign} = this.props;
        
        const className = compact ? 'tag tag-sm' : 'tag';
        const showDeleteButton = canUnassign && !readOnly;

        // It should be null for ravens
        const role = TagsUtil.getHighestRole(tag.role, tag.inheritedRole);

        return (
            <div style={{"--tag-color": tag.style.color, "--tag-font-color": tag.style.fontColor}} className={className} title={tag.name}>
                {role && <TagRoleBadge role={role} />}
                <TagBadge tagType={tag.type} />
                <span>{tag.name}</span>
                {showDeleteButton && <i className="material-icons tag-unassign-btn" onClick={this.onUnassign}>clear</i>}
            </div>
        );
    }
}

class TagForSelecting extends Component {
    
    render() {
        const {tag, readOnly, isGuestTag, isCurrentUser} = this.props;
        
        const checkboxIcon = 'check_box';
        const checkboxClassName = 'checked';

        let isInherited = false;
        let canBeUnassigned = true;
        let canChangeRole = true;
        let role = TagsUtil.getHighestRole(tag.role, tag.inheritedRole);

        if([TAG_USER_TYPES.INHERITED_USER, TAG_USER_TYPES.SHARED_USER].includes(tag.userType)){
            isInherited = true;
        }

        if(readOnly || isInherited){
            canBeUnassigned = false;
        }

        // Inherited user with Admin role (can't make adming of parent tag a guest on a subtag)
        if(readOnly || (isInherited && tag.inheritedRole === TAG_USER_ROLES.ADMIN)){
            canChangeRole = false;
        }

        let tooltip = '';
        
        if(readOnly || isInherited || !canChangeRole){
            if(isCurrentUser){
                tooltip = strings.tags_cant_unassign_current_user;
            }
            else if(isGuestTag){
                tooltip = strings.tags_you_cant_unassign_with_guest_access;
            }
            else if(isInherited){
                if(canChangeRole){
                    tooltip = strings.tags_cant_unassign_inherited_user_but_can_promote;
                }
                else {
                    tooltip = strings.tags_cant_edit_inherited_user;
                }
            }
        }

        return <div className={`list-item-for-selecting n${tag.tagId}`} data-tagid={tag.tagId}>
                    <button disabled={!canBeUnassigned} onClick={() => this.props.onSelectionChange(tag)} className='btn btn-outline-transparent'>
                        <i className={`material-icons selection-icon ${checkboxClassName}`}>{checkboxIcon}</i>
                        <span>{tag.name}</span>
                    </button>
                    {role && 
                        <div className='tag-role'>
                            <TagRolesDropDown  disabled={!canChangeRole} showOptionForAll={false} value={role} onChange={(e) => this.props.onRoleChange(tag, e.target.value)} />
                        </div>
                    }
                    {tooltip && <InfoTooltip message={tooltip} for={`[data-tagid="${tag.tagId}"]`} position="bottom left" noIcon />}
                </div>
    }
}

class TagsListForDisplay extends Component {
    state = {
        isConfirmUnassignDialogOpen: false,
        confirmMessage: '',
    }

    listRef = React.createRef();
    moreRef = React.createRef();
    updateVisibleTagsTimeout = null;

    updateVisibleTags = () => {
        if(!this.props.linear) return;
        if(!this.listRef.current) return;
        if(!this.moreRef.current) return;

        const availableWidth = this.listRef.current.offsetWidth - MORE_TAGS_COUNT_WIDTh;

        const tagsList = this.listRef.current.querySelector('.tags-list');

        if(!tagsList) return;

        const tags = tagsList.querySelectorAll('.tag');

        let visibleTagsWidth = 0;
        let allTagsWidth = 0;
        let numberOfVisibleTags = 0;

        tags.forEach(tag => {
            allTagsWidth += tag.offsetWidth + TAGS_LIST_GAP;

            if(allTagsWidth > availableWidth) return;

            visibleTagsWidth += tag.offsetWidth + TAGS_LIST_GAP;
            numberOfVisibleTags++;
        });
        
        if(allTagsWidth > availableWidth){
            this.moreRef.current.style.display = 'block';            
            this.moreRef.current.textContent = numberOfVisibleTags > 0 ? strings.formatString(strings.tags_more_tags, tags.length - numberOfVisibleTags) : strings.formatString(strings.tags_number_of_tags, tags.length);
            
            tagsList.style.maxWidth = `${visibleTagsWidth}px`;
            tagsList.style.minWidth = `${visibleTagsWidth}px`;
            tagsList.style.width = `${visibleTagsWidth}px`;
        } else {
            this.moreRef.current.style.display = 'none';
            tagsList.style.removeProperty('max-width');
            tagsList.style.removeProperty('min-width');
            tagsList.style.removeProperty('width');
        }
    }

    throttledUpdateVisibleTags = () => {
        if(this.updateVisibleTagsTimeout) clearTimeout(this.updateVisibleTagsTimeout);
        
        this.updateVisibleTagsTimeout = setTimeout(this.updateVisibleTags, 100);
    }

    componentDidMount(){
        if(this.props.linear && this.props.showMore){
            this.updateVisibleTags();
            window.addEventListener('resize', this.throttledUpdateVisibleTags);
        }
    }

    componentDidUpdate(prevProps){
        if(this.props.linear && this.props.showMore){
            if(prevProps.tags && this.props.tags && prevProps.tags.length !== this.props.tags.length){
                this.updateVisibleTags();
            }
        }
    }

    componentWillUnmount(){
        window.removeEventListener('resize', this.throttledUpdateVisibleTags);
    }

    openConfirmUnassignDialog = (tag) => {
        this.setState({
            isConfirmUnassignDialogOpen: true,
            confirmMessage: strings.formatString(strings.tags_unassign_tag_confirm_message, tag.name, this.props.resource),
            tag
        });
    }

    closeConfirmUnassignDialog = () => {
        this.setState({
            isConfirmUnassignDialogOpen: false, 
            confirmMessage: '',
        });
    }

    unassignTag = () => {
        this.props.onUnassign(this.state.tag);
        this.closeConfirmUnassignDialog();
    }

    generateClassName = () => { 
        let className = 'tags-list tags-list-for-display';
        if(this.props.linear) className += ` tags-list-linear`;
        if(this.props.className) className += ` ${this.props.className}`;

        return className;
    }

    getReadonlyTagsIds = () => {
        const readOnlyTagsIds = [];

        if(!this.props.tags) return readOnlyTagsIds;

        if(this.props.isCurrentUser){
            const allTagsIds = this.props.tags.map(tag => tag.tagId);
            readOnlyTagsIds.push(...allTagsIds);
            return readOnlyTagsIds;
        }

        if(this.props.disableGuestTags){
            const guestTagsIds = this.props.tags.filter(tag => !tag.canEdit).map(tag => tag.tagId);
            readOnlyTagsIds.push(...guestTagsIds);
        } 

        if(this.props.disableRootTag){
            const rootTagsIds = this.props.tags.filter(tag => !tag.parentTagId || tag.isRootTag).map(tag => tag.tagId);
            readOnlyTagsIds.push(...rootTagsIds);
        }

        return readOnlyTagsIds;
    }

    render() {
        const {tags} = this.props;
        const listClassName  = this.generateClassName();
        const readOnlyTagsIds = this.getReadonlyTagsIds();

        return (
            <>
            <div ref={this.listRef} className="tags-list-for-display-wrapper">
                <div className={listClassName}>
                    {tags.map(tag => {
                        const isReadOnly = readOnlyTagsIds.includes(tag.tagId);
                        return <TagForDisplay readOnly={isReadOnly} key={tag.tagId} tag={tag} {...this.props} onUnassign={this.openConfirmUnassignDialog} />
                    })}
                </div>
                {this.props.showMore && <div ref={this.moreRef} className='more-tags-count' onClick={this.props.onMore}>+ more</div>}
            </div>

            {this.props.canUnassign && 
                <ConfirmDialog
                    isOpen={this.state.isConfirmUnassignDialogOpen}
                    onOk={this.unassignTag}
                    onCancel={this.closeConfirmUnassignDialog}
                    onDismiss={this.closeConfirmUnassignDialog}
                >
                    <h4>{strings.tags_unassign_tag_confirm_title}</h4>
                    <p>{this.state.confirmMessage}</p>
                </ConfirmDialog> 
            }
            </>
            
        );
    }
}

class TagsListForSelecting extends Component {
    state = {
        isConfirmUnassignDialogOpen: false,
        isConfirmRoleChangeDialogOpen: false,
        confirmMessage: '',
    }

    openConfirmUnassignDialog = (tag) => {
        this.setState({
            isConfirmUnassignDialogOpen: true,
            confirmMessage: strings.formatString(strings.tags_unassign_tag_confirm_message, tag.name, this.props.resource),
            tag
        });
    }

    closeConfirmUnassignDialog = () => {
        this.setState({
            isConfirmUnassignDialogOpen: false, 
            confirmMessage: '',
        });
    }

    unassignTag = () => {
        this.props.onSelectionChange(this.state.tag);
        this.closeConfirmUnassignDialog();
    }

    openConfirmRoleChangeDialog = (tag, role) => {
        const roleString = strings[`tags_role_${role.toLowerCase()}_label`];
        this.setState({
            isConfirmRoleChangeDialogOpen: true,
            confirmMessage: strings.formatString(strings.tags_change_role_confirm_message, this.props.resource, roleString, tag.name),
            tag,
            role
        });
    }

    closeConfirmRoleChangeDialog = () => {
        this.setState({
            isConfirmRoleChangeDialogOpen: false, 
            confirmMessage: '',
        });
    }

    changeRole = () => {
        this.props.onRoleChange(this.state.tag, this.state.role);
        this.closeConfirmRoleChangeDialog();
    }

    getReadonlyTagsIds = () => {
        const readOnlyTagsIds = [];

        if(!this.props.tags) return readOnlyTagsIds;

        if(this.props.isCurrentUser){
            const allTagsIds = this.props.tags.map(tag => tag.tagId);
            readOnlyTagsIds.push(...allTagsIds);
            return readOnlyTagsIds;
        }

        if(this.props.disableGuestTags){
            const guestTagsIds = this.props.tags.filter(tag => !tag.canEdit).map(tag => tag.tagId);
            readOnlyTagsIds.push(...guestTagsIds);
        } 

        if(this.props.disableRootTag){
            const rootTagsIds = this.props.tags.filter(tag => !tag.parentTagId || tag.isRootTag).map(tag => tag.tagId);
            readOnlyTagsIds.push(...rootTagsIds);
        }

        return readOnlyTagsIds;
    }

    render() {
        const {tags, lastTagWarning, showLastTagWarning, isCurrentUser} = this.props;

        const readOnlyTagsIds = this.getReadonlyTagsIds();

        return (
            <>
            <div className={`list-for-selecting ${this.props.className}`}>
                {tags.map(tag => {
                    const isReadOnly = readOnlyTagsIds.includes(tag.tagId);
                    const isGuestTag = !tag.canEdit;
                    return <TagForSelecting readOnly={isReadOnly} isCurrentUser={isCurrentUser} isGuestTag={isGuestTag} key={tag.tagId} tag={tag} onRoleChange={this.openConfirmRoleChangeDialog} onSelectionChange={this.openConfirmUnassignDialog} />
            })}
            </div>

            
            <ConfirmDialog
                isOpen={this.state.isConfirmUnassignDialogOpen}
                onOk={this.unassignTag}
                onCancel={this.closeConfirmUnassignDialog}
                onDismiss={this.closeConfirmUnassignDialog}
            >
                <h4>{strings.tags_unassign_tag_confirm_title}</h4>
                <p>{this.state.confirmMessage}</p>
                {(showLastTagWarning && lastTagWarning) && <p className='warning-message last-tag-warning'><i className='material-icons'>warning</i> {lastTagWarning}</p>}
            </ConfirmDialog> 

            <ConfirmDialog
                isOpen={this.state.isConfirmRoleChangeDialogOpen}
                onOk={this.changeRole}
                onCancel={this.closeConfirmRoleChangeDialog}
                onDismiss={this.closeConfirmRoleChangeDialog}
            >
                <h4>{strings.tags_change_role_confirm_title}</h4>
                <p>{this.state.confirmMessage}</p>
            </ConfirmDialog> 
            
            </>
            
        );
    }
}

export default class TagsList extends Component {
    
    render() {
        const {listType = 'display'} = this.props;

        return listType === 'display' ? 
            <TagsListForDisplay {...this.props} />
         :  <TagsListForSelecting {...this.props} />;
    }
}